/**
 * Borrowing Entity & Guarantor Enums
 *
 * Multi-entity borrower relationships, co-borrowers & guarantor management.
 * Issue #636 — Phase 37
 */

import { z } from 'zod';

// ============================================================================
// BORROWING ENTITY TYPE
// ============================================================================

/**
 * Legal entity types for borrowing entities.
 * Matches the 5 types already in loan-intake EntityTypeSchema.
 */
export enum BorrowingEntityType {
  INDIVIDUAL = 'individual',
  LLC = 'llc',
  CORPORATION = 'corporation',
  TRUST = 'trust',
  PARTNERSHIP = 'partnership',
}

/** Zod schema for BorrowingEntityType validation */
export const BorrowingEntityTypeSchema = z.nativeEnum(BorrowingEntityType);

/** Array of all borrowing entity types for iteration */
export const BORROWING_ENTITY_TYPES = Object.values(BorrowingEntityType);

// ============================================================================
// BORROWING ENTITY STATUS
// ============================================================================

/**
 * Status of a borrowing entity (formation/dissolution lifecycle)
 */
export enum BorrowingEntityStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DISSOLVED = 'dissolved',
}

/** Zod schema for BorrowingEntityStatus validation */
export const BorrowingEntityStatusSchema = z.nativeEnum(BorrowingEntityStatus);

/** Array of all borrowing entity statuses */
export const BORROWING_ENTITY_STATUSES = Object.values(BorrowingEntityStatus);

// ============================================================================
// ENTITY OWNER TYPE
// ============================================================================

/**
 * Whether the owner of a borrowing entity is an individual or another entity.
 * Supports nested ownership structures (e.g., LLC owned by a Trust).
 */
export enum EntityOwnerType {
  INDIVIDUAL = 'individual',
  ENTITY = 'entity',
}

/** Zod schema for EntityOwnerType validation */
export const EntityOwnerTypeSchema = z.nativeEnum(EntityOwnerType);

// ============================================================================
// ENTITY OWNER ROLE
// ============================================================================

/**
 * Role of an owner within a borrowing entity.
 * Different entity types use different roles (e.g., LLC has members, Trust has trustees).
 */
export enum EntityOwnerRole {
  MEMBER = 'member',
  MANAGING_MEMBER = 'managing_member',
  PARTNER = 'partner',
  GENERAL_PARTNER = 'general_partner',
  LIMITED_PARTNER = 'limited_partner',
  TRUSTEE = 'trustee',
  BENEFICIARY = 'beneficiary',
  SHAREHOLDER = 'shareholder',
  OFFICER = 'officer',
}

/** Zod schema for EntityOwnerRole validation */
export const EntityOwnerRoleSchema = z.nativeEnum(EntityOwnerRole);

/** Array of all entity owner roles for iteration */
export const ENTITY_OWNER_ROLES = Object.values(EntityOwnerRole);

// ============================================================================
// GUARANTEE TYPE
// ============================================================================

/**
 * Types of guarantees a guarantor can provide on a loan.
 */
export enum GuaranteeType {
  FULL_RECOURSE = 'full_recourse',
  PARTIAL_RECOURSE = 'partial_recourse',
  PAYMENT = 'payment',
  COMPLETION = 'completion',
  CARVE_OUT = 'carve_out',
}

/** Zod schema for GuaranteeType validation */
export const GuaranteeTypeSchema = z.nativeEnum(GuaranteeType);

/** Array of all guarantee types for iteration */
export const GUARANTEE_TYPES = Object.values(GuaranteeType);

// ============================================================================
// GUARANTOR TYPE
// ============================================================================

/**
 * Whether the guarantor is an individual or an entity (e.g., a trust guaranteeing a loan).
 */
export enum GuarantorType {
  INDIVIDUAL = 'individual',
  ENTITY = 'entity',
}

/** Zod schema for GuarantorType validation */
export const GuarantorTypeSchema = z.nativeEnum(GuarantorType);

// ============================================================================
// GUARANTOR STATUS
// ============================================================================

/**
 * Lifecycle status of a loan guarantor.
 */
export enum GuarantorStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  RELEASED = 'released',
  DEFAULTED = 'defaulted',
}

/** Zod schema for GuarantorStatus validation */
export const GuarantorStatusSchema = z.nativeEnum(GuarantorStatus);

/** Array of all guarantor statuses for iteration */
export const GUARANTOR_STATUSES = Object.values(GuarantorStatus);

// ============================================================================
// CASE BORROWER ROLE
// ============================================================================

/**
 * Role of a borrowing entity on a specific loan/case.
 */
export enum CaseBorrowerRole {
  PRIMARY_BORROWER = 'primary_borrower',
  CO_BORROWER = 'co_borrower',
  SPONSOR = 'sponsor',
}

/** Zod schema for CaseBorrowerRole validation */
export const CaseBorrowerRoleSchema = z.nativeEnum(CaseBorrowerRole);

/** Array of all case borrower roles for iteration */
export const CASE_BORROWER_ROLES = Object.values(CaseBorrowerRole);
